/**
 * Capital Gains Tax on a disposal — THE single implementation.
 *
 * This replaces four independent copies of the same "proportional cost base → assessable gain →
 * marginal rate" math that had drifted apart (`entityDisposalTax.calculateAssetDisposalTax`,
 * `taxAndLoanUtils.calculateCgt`, `retirementVillageCalculations.computeInvestmentPropertyNetForAmount`,
 * `SupportAtHomeHelpers.calculateCgtForStrategy`). They disagreed on ownership splitting, on whether
 * selling costs reduced the gain, and on which reform inputs were passed — so the same asset could
 * produce different CGT depending on which screen the adviser was on. All four now delegate here.
 *
 * WHAT CHANGED vs the old implementations (all deliberate):
 *
 *  1. Tax on the gain is now DIFFERENTIAL, not a flat marginal rate: the tax on the person's income
 *     INCLUDING their share of the gain, less the tax on the same income WITHOUT it. That single change
 *     picks up three things the flat rate missed —
 *       • bracket steps (the old flat top-rate overstated gains that straddled a threshold),
 *       • the 2% Medicare levy on the gain (previously never charged),
 *       • the SAPTO/LITO taper the gain causes (previously the offsets survived the gain intact,
 *         understating tax in the sale year for pensioner clients).
 *     Net direction varies by client; it is now correct rather than conservative-by-accident.
 *
 *  2. Ownership is always honoured. A jointly-owned asset splits 50/50 across both partners' own
 *     positions; `partner`-owned taxes the partner. Previously the aged-care path taxed the entire gain
 *     at the client's rate regardless of ownership, while the retirement village path split correctly.
 *
 *  3. The 1-Jul-2027 reform is actually wired: real disposal date, a non-zero CPI for indexing the
 *     post-commencement slice, and the 30% minimum-tax floor (with its income-support exemption),
 *     which existed in `capitalGainsTax.ts` but was never called from production code.
 *
 * KNOWN LIMITATION (surfaced, not hidden): the fact-find only records acquisition as a `'pre' | 'post'`
 * 1985 flag, not a real date. The reform's straddle apportionment needs an actual acquisition date, so
 * where one is absent we assume a long-held asset (`DEFAULT_ACQUISITION_DATE`) and attach a warning to
 * the result whenever that assumption actually affects the answer — i.e. post-commencement disposals.
 * Advisers can override the CGT figure outright (see `getEffectiveDisposalCgt`).
 */
import { CGT_REGIME } from './constants/cgtReform.ts';
import {
    DEFAULT_ACQUISITION_DATE,
    applyMinimumTax,
    computeCapitalGain,
    isPreCgtAsset,
    resolveAcquisitionDate,
    resolveCostBase,
    yearsBetween,
} from './capitalGainsTax.ts';
import { COMPANY_TAX_RATE, MEDICARE_LEVY_THRESHOLDS } from './constants/ratesAndThresholds.ts';
import {
    getApplicantsCombinedApportionmentPercent,
    getEntityIdFromOwnership,
    isEntityOwnership,
} from './constants/clientStructureTypes.ts';
import { TAX_BRACKETS_WITH_PREV, buildBaseTaxOffsets, calculateIncomeTax } from './incomeTax.ts';
import type { PersonTaxPosition } from './incomeAssessment.ts';
import { DEFAULT_HOME_GROWTH_PCT } from './assetRateOverrides.ts';
import type { FactFindAsset, FactFind, FactFindPerson } from './factFindTypes.ts';

/** Top marginal rate applied to a gain a trust retains rather than distributes. */
export const TRUST_RETAIN_TAX_RATE = 0.45;

/** One person's tax position, as needed to tax an incremental amount of income. */
export interface TaxContextPerson {
    taxableIncome: number;
    frankingCredits: number;
    age: number | null;
    hasPhi: boolean;
}

/**
 * Everything needed to tax an incremental amount of income for this household this year.
 * Produced by `calculateFinancials` (returned as `financials.taxContext`); the fields mirror what
 * `incomeTax.ts`'s household assessor takes, because that assessor — THE tax system, not a copy of
 * it — is what re-taxes the household with and without the gain.
 */
export interface DisposalTaxContext {
    /** The year's rate bag: `taxBrackets`, `medicareLevyThresholds` and (optionally) `taxOffsets`. */
    rates: any;
    relationshipStatus: string;
    isPartnered: boolean;
    /** Illness-separated couples are each entitled to the higher SAPTO band. */
    illnessSeparated: boolean;
    receivesIncomeSupport: boolean;
    client: TaxContextPerson;
    partner: TaxContextPerson;
}

export type DisposalOwner = 'client' | 'partner';

/** Who bears the tax on the gain. Determined by ownership and, for entities, the structure type. */
export type DisposalTaxMethod =
    | 'personal_marginal'
    | 'trust_distribute'
    | 'trust_retain'
    | 'company_rate'
    | 'partnership_apportioned';

export interface DisposalCgtInput {
    /** Household tax position. Prefer passing `financials.taxContext`; see `resolveDisposalTaxContext`. */
    taxContext: DisposalTaxContext;
    asset?: FactFindAsset | null;
    /** Gross proceeds for the portion being disposed of. */
    salePrice: number;
    sellingCosts?: number;
    /**
     * Asset value BEFORE the disposal, when only part is being sold. The cost base is apportioned by
     * `salePrice / originalAssetValue`. Omit for a whole-asset sale.
     */
    originalAssetValue?: number;
    /** Explicit cost base (e.g. a UI field). Falls back to `asset.costBase`, then the 25% assumption. */
    costBaseOverride?: number | null;
    /** Disposal date — decides which CGT regime applies. Defaults to today. */
    disposalDate?: Date | string;
    /** CPI for indexing the post-commencement slice. Defaults to `CGT_REGIME.defaultCpiRate`. */
    cpiRate?: number;
    /** Structures list, for entity-owned assets. Defaults to none. */
    structures?: FactFind['structures'];
    /** How a trust deals with the gain. `retain` taxes it in the trust at the top rate. */
    trustDisposal?: 'retain' | 'distribute';
    /**
     * Today, for valuing a former home back to the day it was first rented (the cost base reset).
     * Defaults to now; tests pin it.
     */
    valuationDate?: Date | string;
}

/** Six years: the main-residence absence rule (ITAA 1997 s118-145). */
export const MAIN_RESIDENCE_ABSENCE_YEARS = 6;

/**
 * THE FORMER HOME (owner, 8 Oct 2026). An investment property the client lived in before moving to
 * a granny flat, a village, a rental or family, and has rented out since (`formerHome` with
 * `firstRentedOn`), is sold under the main-residence rules, not as an ordinary investment:
 *   - the cost base is reset to the property's value on the day it was first rented
 *     (s118-192) — here today's value taken back at its growth rate, as a stated estimate;
 *   - a sale within six years of that day is fully exempt (s118-145), provided no other dwelling
 *     is claimed — a granny-flat right, a village unit or a rental is not one the client owns;
 *   - a sale after six years is taxed on the part of the time since first renting that is beyond
 *     six years (s118-185: the gain apportioned by days), with the 50% discount as usual.
 * Returns null where the rule does not apply (not a former home, or no date).
 */
export function formerHomeAbsence(asset: FactFindAsset | null | undefined, disposalDate: Date | string, valuationDate: Date | string = new Date()): { firstRentedOn: Date; yearsSinceFirstRented: number; exempt: boolean; taxableFraction: number; costBaseReset: number } | null {
    if (!asset?.formerHome) return null;
    const raw = String(asset.firstRentedOn || '');
    if (!/^\d{4}-\d{2}$/.test(raw)) return null;
    const firstRentedOn = new Date(raw + '-01T00:00:00');
    if (!Number.isFinite(firstRentedOn.getTime())) return null;
    const yearsSinceFirstRented = Math.max(0, yearsBetween(firstRentedOn, disposalDate));
    const exempt = yearsSinceFirstRented <= MAIN_RESIDENCE_ABSENCE_YEARS;
    const taxableFraction = exempt ? 0 : Math.max(0, Math.min(1, (yearsSinceFirstRented - MAIN_RESIDENCE_ABSENCE_YEARS) / yearsSinceFirstRented));
    const value = Math.max(0, Number(asset.value) || 0);
    const growth = asset.growthRate != null && Number.isFinite(Number(asset.growthRate)) ? Number(asset.growthRate) : DEFAULT_HOME_GROWTH_PCT;
    const yearsToToday = Math.max(0, yearsBetween(firstRentedOn, valuationDate));
    const costBaseReset = value / Math.pow(1 + growth / 100, yearsToToday);
    return { firstRentedOn, yearsSinceFirstRented, exempt, taxableFraction, costBaseReset };
}

export interface DisposalCgtPersonDetail {
    owner: DisposalOwner;
    /** This person's share of the ASSESSABLE (post-discount / post-indexation) gain. */
    assessableGainShare: number;
    cgt: number;
    /** cgt / assessableGainShare — an effective rate, since the calc is differential not flat. */
    effectiveRate: number;
    /** How much of `cgt` came from the 30% minimum-tax floor rather than the marginal calculation. */
    minimumTaxUplift: number;
}

// A type alias rather than an interface deliberately: TypeScript grants type aliases an implicit
// index signature, which lets this flow into the looser `DisposalTaxSummary` bag the UI panels use.
export type DisposalCgtResult = {
    salePrice: number;
    sellingCosts: number;
    isPreCGT: boolean;
    costBase: number;
    capitalGain: number;
    assessableCapitalGain: number;
    estimatedCGT: number;
    netProceeds: number;
    taxMethod: DisposalTaxMethod;
    entityDisposalWarning: string | null;
    /** Set when the answer depends on an assumed acquisition date — advisers should confirm/override. */
    acquisitionDateAssumed: boolean;
    disposalPhase: 'old' | 'new' | 'straddle';
    perPerson: DisposalCgtPersonDetail[];
    // --- Backwards-compatible fields consumed by existing panels/allocators ---
    clientEstimatedCgt: number | null;
    partnerEstimatedCgt: number | null;
    clientAssessableGainShare: number | null;
    partnerAssessableGainShare: number | null;
    clientMarginalRate: number | null;
    partnerMarginalRate: number | null;
    apportionmentClientPct: number | null;
    apportionmentPartnerPct: number | null;
    structureName: string | null;
};

/**
 * Fallback context for callers that only have the two assessable-income figures and not a full
 * `financials.taxContext` (e.g. a stale cached payload from before `taxContext` was returned).
 *
 * Ages and PHI status are unknown here, so SAPTO cannot be assessed and the Medicare levy falls back
 * to the un-indexed current-year thresholds. Prefer `resolveDisposalTaxContext` — this exists so a
 * missing context degrades to the previous behaviour instead of throwing.
 */
export function taxContextFromAssessableIncomes(
    clientIncome: number,
    partnerIncome: number,
    isPartnered: boolean,
): DisposalTaxContext {
    return {
        rates: { taxBrackets: TAX_BRACKETS_WITH_PREV, medicareLevyThresholds: MEDICARE_LEVY_THRESHOLDS, taxOffsets: buildBaseTaxOffsets() },
        relationshipStatus: isPartnered ? 'couple' : 'single',
        isPartnered,
        illnessSeparated: false,
        receivesIncomeSupport: false,
        client: { taxableIncome: clientIncome || 0, frankingCredits: 0, age: null, hasPhi: false },
        partner: { taxableIncome: partnerIncome || 0, frankingCredits: 0, age: null, hasPhi: false },
    };
}

/** A person's stored figures as the `PersonTaxPosition` the income-tax assessor takes. */
function taxPositionOf(person: TaxContextPerson, extraTaxableIncome: number): PersonTaxPosition {
    return {
        salary: person.taxableIncome + extraTaxableIncome,
        investmentSources: [],
        frankingCredits: person.frankingCredits,
        deductionSources: [],
    };
}

/**
 * The extra tax this person pays if `extraTaxableIncome` is added to their income — brackets, Medicare
 * levy (including the couple family thresholds) and the SAPTO/LITO taper all included, because the
 * household is re-assessed both ways by `incomeTax.ts`'s own assessor. Nothing here re-implements a
 * tax rule: the gain is taxed by the same code that taxes every other dollar in the product.
 */
function taxOnExtraIncome(owner: DisposalOwner, extraTaxableIncome: number, ctx: DisposalTaxContext): number {
    if (!(extraTaxableIncome > 0)) return 0;
    const assess = (extraClient: number, extraPartner: number) =>
        calculateIncomeTax({
            rates: ctx.rates,
            clientTaxable: taxPositionOf(ctx.client, extraClient),
            partnerTaxable: taxPositionOf(ctx.partner, extraPartner),
            isPartnered: ctx.isPartnered,
            illnessSeparated: ctx.illnessSeparated,
            relationshipStatus: ctx.relationshipStatus,
            client: { phi: ctx.client.hasPhi } as FactFindPerson,
            partner: { phi: ctx.partner.hasPhi } as FactFindPerson,
            clientAge: ctx.client.age,
            partnerAge: ctx.partner.age,
        })[owner].netTax;
    const before = assess(0, 0);
    const after = assess(owner === 'client' ? extraTaxableIncome : 0, owner === 'partner' ? extraTaxableIncome : 0);
    // Clamped at 0: a gain can never reduce tax, and franking-credit refunds must not net off here —
    // the refund is already reported in the year's cashflow.
    return Math.max(0, after - before);
}

/**
 * One person's CGT on their share, including the reform's 30% floor on the real (post-commencement)
 * portion. The differential calculation yields a single figure for the whole share, so the pre- and
 * post-commencement portions are attributed pro-rata by assessable amount before the floor is applied
 * to the post portion only.
 */
function personCgt(
    owner: DisposalOwner,
    preShare: number,
    postShare: number,
    ctx: DisposalTaxContext,
): DisposalCgtPersonDetail {
    const assessableGainShare = preShare + postShare;
    if (!(assessableGainShare > 0)) {
        return { owner, assessableGainShare: 0, cgt: 0, effectiveRate: 0, minimumTaxUplift: 0 };
    }
    const marginalTax = taxOnExtraIncome(owner, assessableGainShare, ctx);
    const postPortionOfTax = marginalTax * (postShare / assessableGainShare);
    const prePortionOfTax = marginalTax - postPortionOfTax;
    const postAfterFloor = applyMinimumTax({
        postCommencementTaxable: postShare,
        marginalTaxOnPost: postPortionOfTax,
        isIncomeSupportRecipient: ctx.receivesIncomeSupport,
    });
    const cgt = prePortionOfTax + postAfterFloor;
    return {
        owner,
        assessableGainShare,
        cgt,
        effectiveRate: cgt / assessableGainShare,
        minimumTaxUplift: Math.max(0, postAfterFloor - postPortionOfTax),
    };
}

/** Split an assessable gain across the applicants according to the asset's ownership. */
function splitByOwnership(ownership: string | undefined, isPartnered: boolean): { client: number; partner: number } {
    if (!isPartnered) return { client: 1, partner: 0 };
    if (ownership === 'partner') return { client: 0, partner: 1 };
    if (ownership === 'joint') return { client: 0.5, partner: 0.5 };
    // 'client' and anything unrecognised. Note the old taxAndLoanUtils.calculateCgt returned ZERO CGT
    // for an unrecognised ownership (including entity-owned assets), silently under-taxing the sale.
    return { client: 1, partner: 0 };
}

/**
 * CGT on a disposal. Pure — takes a resolved tax context rather than `factFind` so it can be called
 * from a projection year without recomputing the household position.
 */
export function computeDisposalCgt(input: DisposalCgtInput): DisposalCgtResult {
    const {
        taxContext: ctx,
        asset,
        salePrice = 0,
        sellingCosts = 0,
        originalAssetValue,
        costBaseOverride,
        disposalDate = new Date(),
        cpiRate = CGT_REGIME.defaultCpiRate,
        structures,
        trustDisposal,
        valuationDate = new Date(),
    } = input;

    const netSaleProceeds = salePrice - sellingCosts;
    // A former home rented out: the main-residence absence rule (see formerHomeAbsence).
    const absence = formerHomeAbsence(asset, disposalDate, valuationDate);
    const ownership = asset?.ownership;
    const entityId = isEntityOwnership(ownership) ? getEntityIdFromOwnership(ownership) : null;
    const structure = entityId ? (structures || []).find((s) => s.id === entityId) : null;
    const structureName = structure?.name?.trim() || null;

    // Who bears the tax. Resolved up front so that a pre-CGT / zero-gain disposal still reports the
    // structure it WOULD have been taxed through (panels key their explanatory copy off this).
    const structureType = structure?.type || (entityId ? 'family_trust' : null);
    const taxMethod: DisposalTaxMethod = !entityId
        ? 'personal_marginal'
        : structureType === 'private_company'
          ? 'company_rate'
          : structureType === 'partnership'
            ? 'partnership_apportioned'
            : trustDisposal === 'retain'
              ? 'trust_retain'
              : 'trust_distribute';

    const isPreCGT = isPreCgtAsset(asset) || !!absence?.exempt;
    const baseResult = {
        salePrice,
        sellingCosts,
        isPreCGT,
        perPerson: [] as DisposalCgtPersonDetail[],
        clientEstimatedCgt: null,
        partnerEstimatedCgt: null,
        clientAssessableGainShare: null,
        partnerAssessableGainShare: null,
        clientMarginalRate: null,
        partnerMarginalRate: null,
        apportionmentClientPct: null,
        apportionmentPartnerPct: null,
        structureName,
    };

    // --- Cost base (apportioned when only part of the holding is sold) ---
    const valueForCostBase = originalAssetValue != null && originalAssetValue > 0 ? originalAssetValue : salePrice;
    const explicitCostBase = costBaseOverride != null && costBaseOverride > 0 ? costBaseOverride : null;
    const totalCostBase = isPreCGT
        ? 0
        : absence
            ? absence.costBaseReset
            : (explicitCostBase ?? resolveCostBase({ costBase: asset?.costBase, value: valueForCostBase }).costBase);
    const proportionalCostBase =
        originalAssetValue != null && originalAssetValue > 0 && salePrice < originalAssetValue
            ? totalCostBase * (salePrice / originalAssetValue)
            : totalCostBase;

    if (isPreCGT || !(salePrice > 0)) {
        return {
            ...baseResult,
            costBase: proportionalCostBase,
            capitalGain: 0,
            assessableCapitalGain: 0,
            estimatedCGT: 0,
            netProceeds: netSaleProceeds,
            taxMethod,
            entityDisposalWarning: null,
            acquisitionDateAssumed: false,
            disposalPhase: 'old',
        };
    }

    // Selling costs reduce the gain (they form part of the cost base for CGT purposes).
    const capitalGain = Math.max(0, netSaleProceeds - proportionalCostBase);

    // --- Assessable gain under the applicable regime ---
    // A recorded acquisition date drives BOTH the 12-month 50% discount test and the 1-Jul-2027 split.
    // Where it is absent we fall back to a long-held asset, which grants the discount — so an asset
    // actually bought within the last year would be under-taxed. That is why the absence is reported
    // (see `acquisitionDateAssumed`) and prompted for in the fact-find rather than silently absorbed.
    const recordedAcquisition = absence ? absence.firstRentedOn : resolveAcquisitionDate(asset);
    const gain = computeCapitalGain({
        costBase: proportionalCostBase,
        proceeds: netSaleProceeds,
        acquisitionDate: recordedAcquisition ?? DEFAULT_ACQUISITION_DATE,
        disposalDate,
        cpiRate,
        config: CGT_REGIME,
    });
    // A former home sold after six years away: only the time beyond six years is taxed.
    const assessableCapitalGain = absence ? gain.totalTaxable * absence.taxableFraction : gain.totalTaxable;
    // Only worth raising where it can actually change the answer: there has to be a gain to tax.
    const dateAssumptionMatters = !recordedAcquisition && assessableCapitalGain > 0;
    const acquisitionWarning = dateAssumptionMatters
        ? 'No acquisition date is recorded for this asset, so it is assumed to have been held for more than 12 months (which allows the 50% CGT discount) and to have been acquired before the 1 July 2027 CGT changes. If it was bought within the last 12 months the CGT would be higher. Record the month acquired, or override the CGT estimate.'
        : null;

    const finish = (fields: {
        assessableCapitalGain: number;
        estimatedCGT: number;
        taxMethod: DisposalTaxMethod;
        warning: string | null;
        perPerson?: DisposalCgtPersonDetail[];
        apportionmentClientPct?: number | null;
        apportionmentPartnerPct?: number | null;
    }): DisposalCgtResult => {
        const clientDetail = fields.perPerson?.find((p) => p.owner === 'client') || null;
        const partnerDetail = fields.perPerson?.find((p) => p.owner === 'partner') || null;
        return {
            ...baseResult,
            costBase: proportionalCostBase,
            capitalGain,
            assessableCapitalGain: fields.assessableCapitalGain,
            estimatedCGT: fields.estimatedCGT,
            netProceeds: netSaleProceeds - fields.estimatedCGT,
            taxMethod: fields.taxMethod,
            entityDisposalWarning: fields.warning || acquisitionWarning,
            acquisitionDateAssumed: dateAssumptionMatters,
            disposalPhase: gain.phase,
            perPerson: fields.perPerson || [],
            clientEstimatedCgt: clientDetail ? clientDetail.cgt : null,
            partnerEstimatedCgt: partnerDetail ? partnerDetail.cgt : null,
            clientAssessableGainShare: clientDetail ? clientDetail.assessableGainShare : null,
            partnerAssessableGainShare: partnerDetail ? partnerDetail.assessableGainShare : null,
            clientMarginalRate: clientDetail ? clientDetail.effectiveRate : null,
            partnerMarginalRate: partnerDetail ? partnerDetail.effectiveRate : null,
            apportionmentClientPct: fields.apportionmentClientPct ?? null,
            apportionmentPartnerPct: fields.apportionmentPartnerPct ?? null,
        };
    };

    if (assessableCapitalGain <= 0) {
        return finish({ assessableCapitalGain: 0, estimatedCGT: 0, taxMethod, warning: null });
    }

    // --- Entity structures that tax the gain in the entity, not in the applicants' hands ---
    if (taxMethod === 'company_rate') {
        // No CGT discount for companies — the whole nominal gain is taxed at the company rate.
        return finish({
            assessableCapitalGain: capitalGain,
            estimatedCGT: capitalGain * COMPANY_TAX_RATE,
            taxMethod,
            warning:
                'Gain taxed in the company. Cash remains in the company until distributed — confirm dividends and franking with your accountant.',
        });
    }

    if (taxMethod === 'trust_retain') {
        return finish({
            assessableCapitalGain: capitalGain,
            estimatedCGT: capitalGain * TRUST_RETAIN_TAX_RATE,
            taxMethod,
            warning: 'Gain retained in trust — top trust rate applied. Confirm with your accountant.',
        });
    }

    // --- Taxed in the applicants' hands ---
    // Entity (distributing trust / partnership): split by the structure's apportionment percentages.
    // Personally held: split by the asset's ownership.
    let clientFraction: number;
    let partnerFraction: number;
    let apportionmentClientPct: number | null = null;
    let apportionmentPartnerPct: number | null = null;

    if (entityId) {
        const clientPct = Math.min(100, Math.max(0, structure?.apportionmentClient || 0));
        const partnerPct = ctx.isPartnered ? Math.min(100, Math.max(0, structure?.apportionmentPartner || 0)) : 0;
        const combined = getApplicantsCombinedApportionmentPercent(structure as any, ctx.isPartnered) || 100;
        clientFraction = clientPct / combined;
        partnerFraction = partnerPct / combined;
        apportionmentClientPct = clientPct;
        apportionmentPartnerPct = partnerPct;
    } else {
        const split = splitByOwnership(ownership, ctx.isPartnered);
        clientFraction = split.client;
        partnerFraction = split.partner;
    }

    const perPerson: DisposalCgtPersonDetail[] = [];
    if (clientFraction > 0) {
        perPerson.push(personCgt('client', gain.preCommencementTaxable * clientFraction, gain.postCommencementTaxable * clientFraction, ctx));
    }
    if (partnerFraction > 0) {
        perPerson.push(personCgt('partner', gain.preCommencementTaxable * partnerFraction, gain.postCommencementTaxable * partnerFraction, ctx));
    }

    return finish({
        assessableCapitalGain,
        estimatedCGT: perPerson.reduce((sum, p) => sum + p.cgt, 0),
        taxMethod,
        warning: null,
        perPerson,
        apportionmentClientPct,
        apportionmentPartnerPct,
    });
}
